import { ChangeEvent, useEffect, useState } from "react";
import styles from "./RangeFilter.module.scss";
import { Button } from "../../buttons";
import { useDebounce } from "../../../helpers/debounce/debounce";
import * as Popover from "@radix-ui/react-popover";
import { IconException } from "../../icons";
import { ButtonExpandable } from "../../buttons/button-expandable/button-expandable";

export interface IRangeFilterProps {
  /** The text to be displayed on the filter's button. */
  label: string;
  /** The  method that will be called when the range values change. */
  onChange: (emittedValues?: {
    min: number | null;
    max: number | null;
  }) => void;
  /** The text to be displayed on the right of each range input. */
  valueLabel?: string;
  /**Is the filter disabled? */
  disabled?: boolean;
  /** The default minimum value. */
  defaultMin?: number;
  /** The default maximum value. */
  defaultMax?: number /** The minimum value, if it is externally managed */;
  currentMin?: number | null;
  /** The maximum value, if it is externally managed */
  currentMax?: number | null;
}

export const useRangeFilters = (
  onChange: (emittedValues?: {
    min: number | null;
    max: number | null;
  }) => void,
  defaultMin?: number,
  defaultMax?: number,
  disabled?: boolean,
  shouldDebounce: boolean = true,
  currentMin?: number | null,
  currentMax?: number | null,
) => {
  const [minRange, setMinRange] = useState<number | null>(null);
  const [maxRange, setMaxRange] = useState<number | null>(null);
  const [isInitialRender, setIsInitialRender] = useState(true);

  // error related variables
  const [showError, setShowError] = useState<boolean>(false);
  const [lastInputted, setLastInputted] = useState<null | "min" | "max">(null);

  const errorCondition =
    minRange !== null && maxRange !== null && maxRange < minRange;
  const showRangeCondition = minRange !== null || maxRange !== null;
  const isResetButtonDisabled =
    minRange === defaultMin && maxRange === defaultMax;

  const onRangeClearClick = () => {
    if (!disabled) {
      setMinRange(null);
      setMaxRange(null);
    }
  };

  const onRangeResetClick = () => {
    if (!disabled) {
      setMinRange(defaultMin ?? null);
      setMaxRange(defaultMax ?? null);
    }
  };

  // when either of the range input values change
  const handleRangeChange = (
    event: ChangeEvent<HTMLInputElement>,
    key: "min" | "max",
  ) => {
    const rangeInputValue = Number(event.target.value);

    setShowError(false);
    if (key === "min") {
      setLastInputted("min");
      if (event.target.value === "") {
        setMinRange(null);
      } else {
        setMinRange(rangeInputValue);
      }
    } else if (key === "max") {
      setLastInputted("max");
      if (event.target.value === "") {
        setMaxRange(null);
      } else {
        setMaxRange(rangeInputValue);
      }
    }
  };

  const emitValues = () => {
    if (!errorCondition) {
      setShowError(false);
      onChange({ min: minRange ?? null, max: maxRange ?? null });
    } else {
      setShowError(true);
    }
  };

  // prevents a user giving min = 100 and max = 0
  const setRangeWithValidation = (min?: number | null, max?: number | null) => {
    if (
      max !== null &&
      min !== null &&
      max !== undefined &&
      min !== undefined &&
      max < min
    ) {
      setMinRange(Number(min));
      setMaxRange(Number(min));
    } else {
      if (min !== null && min !== undefined) {
        setMinRange(Number(min));
      }
      if (max !== null && max !== undefined) {
        setMaxRange(Number(max));
      }
    }
  };

  const debouncedEmitValues = useDebounce(emitValues, 1300);

  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
      setRangeWithValidation(defaultMin, defaultMax);
    } else if (shouldDebounce) {
      debouncedEmitValues.debounce();
    } else {
      emitValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minRange, maxRange]);

  useEffect(() => {
    if (currentMin !== undefined) {
      setMinRange(currentMin);
    }
  }, [currentMin]);

  useEffect(() => {
    if (currentMax !== undefined) {
      setMaxRange(currentMax);
    }
  }, [currentMax]);

  return {
    minRange,
    maxRange,
    handleRangeChange,
    onRangeClearClick,
    onRangeResetClick,
    showError,
    errorCondition,
    lastInputted,
    showRangeCondition,
    isResetButtonDisabled,
  };
};

export const RangeFilter = (props: IRangeFilterProps) => {
  const {
    minRange,
    maxRange,
    handleRangeChange,
    onRangeClearClick,
    showError,
    errorCondition,
    lastInputted,
    showRangeCondition,
  } = useRangeFilters(
    props.onChange,
    props.defaultMin,
    props.defaultMax,
    props.disabled,
  );

  const [isOpen, setIsOpen] = useState(false);

  // error related variables
  const minError = "Must be less than the max";
  const maxError = "Must be greater than the min";

  // text label to display on the right of each range input box
  const valueLabel = <p>{props.valueLabel}</p>;

  // button styling
  const buttonStyling = [
    showRangeCondition ? styles.givenValue : "",
    isOpen ? styles.opened : "",
    // props.disabled ? genericStyles.disabledButton : "",
    errorCondition && showError ? styles.errorButton : "",
  ].join(" ");

  // text to display on the button
  let buttonLabelDisplay = props.label;
  if (errorCondition && showError) {
    buttonLabelDisplay = "Invalid Range";
  } else if (minRange !== null && maxRange !== null) {
    buttonLabelDisplay = `${minRange} - ${maxRange}`;
  } else if (minRange !== null) {
    buttonLabelDisplay = `Min = ${minRange}`;
  } else if (maxRange !== null) {
    buttonLabelDisplay = `Max = ${maxRange}`;
  }

  const handleOpen = (openValue: boolean): void => {
    if (!props.disabled) {
      setIsOpen(openValue);
    }
  };

  return (
    <Popover.Root modal={false} open={isOpen} onOpenChange={handleOpen}>
      <Popover.Trigger asChild>
        <ButtonExpandable
          variant="secondary"
          data-testid="button"
          disabled={props.disabled}
          className={buttonStyling}
          expanded={isOpen}
          onExpandChange={handleOpen}
        >
          {buttonLabelDisplay}
        </ButtonExpandable>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          onFocusOutside={(e: Event) => e.preventDefault()}
          align={"start"}
          className={styles.dropdownMenuContent}
          data-testid="dropdown-content"
        >
          <div className={styles.rangeFilterNameHeader}>
            <p className={styles.rangeFilterNameHeaderText}>{props.label}</p>
          </div>
          <div className={styles.rangeMenuItem}>
            <p className={styles.rangeDescriptionText}>Min</p>
            <input
              className={`${styles.rangeInput} ${
                errorCondition && showError && lastInputted === "min"
                  ? styles.inputError
                  : ""
              }`}
              data-testid="min-input"
              type={"number"}
              onChange={(event) => {
                handleRangeChange(event, "min");
              }}
              key={"min"}
              value={minRange !== null ? minRange : ""}
            />{" "}
            <span className={styles.valueLabel}>{valueLabel}</span>
          </div>
          {errorCondition && showError && lastInputted === "min" ? (
            <div className={styles.errorContainer}>
              <IconException />
              <span className={styles.errorText}>{minError}</span>
            </div>
          ) : (
            <></>
          )}
          <div className={styles.rangeMenuItem}>
            <p className={styles.rangeDescriptionText}>Max</p>
            <input
              className={`${styles.rangeInput} ${
                errorCondition && showError && lastInputted === "max"
                  ? styles.inputError
                  : ""
              }`}
              data-testid="max-input"
              type={"number"}
              onChange={(event) => {
                handleRangeChange(event, "max");
              }}
              key={"max"}
              value={maxRange !== null ? maxRange : ""}
            />{" "}
            <span className={styles.valueLabel}>{valueLabel}</span>
          </div>
          {errorCondition && showError && lastInputted === "max" ? (
            <div className={styles.errorContainer}>
              <IconException />
              <span className={styles.errorText}>{maxError}</span>
            </div>
          ) : (
            <></>
          )}
          <div className={styles.clearContainer}>
            <Button
              onClick={onRangeClearClick}
              disabled={!showRangeCondition}
              variant="tertiary-blue"
            >
              Clear
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
